import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useKarmaTotalQuery } from '@/entities/karma';
import { KarmaCoin, KarmaCountUp, pickKarmaQuote } from '@/shared/ui/primitives';
import type { KarmaQuote } from '@/shared/ui/primitives';

function useDrain(totalMs: number) {
  const [pct, setPct] = useState(100);

  useEffect(() => {
    const start = Date.now();
    let id: number;

    function tick() {
      const pctLeft = Math.max(0, 100 - ((Date.now() - start) / totalMs) * 100);
      setPct(pctLeft);
      if (pctLeft > 0) id = requestAnimationFrame(tick);
    }

    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [totalMs]);

  return pct;
}

// ─── Toast card ───────────────────────────────────────────────────────────────

const DURATION_MS = 6000;

interface KarmaToastCardProps {
  delta: number;
  quote: KarmaQuote;
  onDismiss: () => void;
  onViewKarma: () => void;
}

function KarmaToastCard({ delta, quote, onDismiss, onViewKarma }: KarmaToastCardProps) {
  const drain = useDrain(DURATION_MS);

  return (
    <div
      className="relative flex w-80 cursor-default items-start gap-4 overflow-hidden rounded-2xl p-4"
      style={{
        background: 'linear-gradient(160deg, rgba(22,14,2,0.98) 0%, rgba(30,18,4,0.98) 100%)',
        border: '1px solid rgba(251,191,36,0.25)',
        boxShadow: '0 0 0 1px rgba(251,191,36,0.08), 0 20px 48px rgba(0,0,0,0.55)',
        backdropFilter: 'blur(20px)',
        animation: 'kt-slide-in 0.5s cubic-bezier(0.34,1.56,0.64,1) both',
      }}
    >
      {/* Glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 10% 0%, rgba(251,191,36,0.09), transparent)',
        }}
        aria-hidden="true"
      />

      {/* Top edge shimmer */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(251,191,36,0.45), transparent)',
        }}
        aria-hidden="true"
      />

      <KarmaCoin size={44} className="mt-0.5" />

      {/* Body */}
      <div className="min-w-0 flex-1" style={{ animation: 'kt-fadein 0.35s ease-out 0.1s both' }}>
        <div className="flex items-baseline gap-1.5">
          <span
            className="text-3xl font-black tabular-nums leading-none"
            style={{ color: '#fde68a', textShadow: '0 0 22px rgba(251,191,36,0.4)' }}
          >
            +<KarmaCountUp value={delta} />
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-widest text-yellow-400/60">
            karma
          </span>
        </div>

        <p className="mt-1.5 text-[13px] font-semibold leading-snug text-white/90 italic">
          "{quote.text}"
        </p>
        {quote.attribution && (
          <p className="mt-0.5 text-xs leading-snug text-white/35 not-italic">
            — {quote.attribution}
          </p>
        )}

        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            onClick={onViewKarma}
            className="rounded-md px-3 py-1.5 text-xs font-semibold transition-opacity hover:opacity-85 active:opacity-70"
            style={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', color: '#1c0d00' }}
          >
            View karma
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="text-xs text-white/35 transition-colors hover:text-white/60"
          >
            Dismiss
          </button>
        </div>
      </div>

      {/* Drain bar */}
      <div className="absolute inset-x-0 bottom-0 h-[2px] overflow-hidden rounded-b-2xl">
        <div
          className="h-full"
          style={{
            width: `${drain}%`,
            background: 'linear-gradient(90deg, #f59e0b, #fde68a, #f59e0b)',
            transition: 'none',
          }}
        />
      </div>

      <style>{`
        @keyframes kt-slide-in {
          0%   { opacity: 0; transform: translateX(110%) scale(0.88) rotate(1.5deg); }
          60%  { opacity: 1; transform: translateX(-3%) scale(1.02) rotate(-0.4deg); }
          80%  { transform: translateX(1.5%) scale(0.995) rotate(0deg); }
          100% { opacity: 1; transform: translateX(0) scale(1) rotate(0deg); }
        }
        @keyframes kt-fadein {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ─── Root component ───────────────────────────────────────────────────────────

/**
 * Detects karma total increases and fires a top-right toast. Action-agnostic.
 */
export function KarmaAwardToast() {
  const navigate = useNavigate();
  const { data } = useKarmaTotalQuery();
  const previousTotalRef = useRef<number | null>(null);

  useEffect(() => {
    if (data === undefined) return;

    const current = data.earnings;
    const previous = previousTotalRef.current;

    if (previous === null) {
      previousTotalRef.current = current;
      return;
    }

    const delta = current - previous;
    previousTotalRef.current = current;

    if (delta > 0) {
      const quote = pickKarmaQuote();
      toast.custom(
        (id) => (
          <KarmaToastCard
            delta={delta}
            quote={quote}
            onDismiss={() => toast.dismiss(id)}
            onViewKarma={() => {
              toast.dismiss(id);
              navigate('/karma');
            }}
          />
        ),
        { duration: DURATION_MS, position: 'top-right' }
      );
    }
  }, [data, navigate]);

  return null;
}
